export type MediaType = 'image' | 'video' | 'audio' | 'pdf'
export type MediaDisplayMode = 'standalone' | 'modal' | 'split' | 'panel'

export type MediaMaterial = {
  nodeId?: string
  name: string
  url: string
  type: MediaType
  skippable?: boolean
  completionRequired?: boolean
}

/** The media a show-media node is currently presenting (panel mode never blocks). */
export type ShowingMedia = {
  mediaType: MediaType
  mediaName: string
  mediaId: string
  displayMode: 'standalone' | 'modal' | 'split'
  mediaTypeRight?: MediaType
  splitSwap?: boolean
}

export function youtubeEmbedUrl(url: string, autoplay = false): string | null {
  try {
    const parsed = new URL(url)
    const host = parsed.hostname.replace(/^www\./, '')
    let videoId = ''
    if (host === 'youtu.be') videoId = parsed.pathname.split('/').filter(Boolean)[0] ?? ''
    else if (host === 'youtube.com' || host === 'm.youtube.com') {
      videoId = parsed.searchParams.get('v') ?? ''
      if (!videoId && parsed.pathname.startsWith('/shorts/'))
        videoId = parsed.pathname.split('/')[2] ?? ''
      if (!videoId && parsed.pathname.startsWith('/embed/'))
        videoId = parsed.pathname.split('/')[2] ?? ''
    }
    if (!/^[A-Za-z0-9_-]{6,}$/.test(videoId)) return null
    const embed = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`)
    embed.searchParams.set('rel', '0')
    embed.searchParams.set('enablejsapi', '1')
    embed.searchParams.set('origin', window.location.origin)
    if (autoplay) {
      embed.searchParams.set('autoplay', '1')
      embed.searchParams.set('controls', '0')
      embed.searchParams.set('disablekb', '1')
    }
    return embed.toString()
  } catch {
    return null
  }
}

export function vimeoEmbedUrl(url: string, autoplay = false): string | null {
  try {
    const parsed = new URL(url)
    const host = parsed.hostname.replace(/^www\./, '')
    if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null
    const segments = parsed.pathname.split('/').filter(Boolean)
    let videoId = ''
    let hash = ''
    if (host === 'player.vimeo.com') {
      const idx = segments.indexOf('video')
      videoId = (idx >= 0 ? segments[idx + 1] : segments[0]) ?? ''
      hash = (idx >= 0 ? segments[idx + 2] : segments[1]) ?? ''
    } else {
      // Plain vimeo.com URLs: /123456, /channels/x/123456, /groups/x/videos/123456,
      // optionally followed by a private-video hash segment: /123456/abcDEF123
      const idPos = segments.findIndex((s) => /^\d+$/.test(s))
      videoId = (idPos >= 0 ? segments[idPos] : '') ?? ''
      const next = idPos >= 0 ? segments[idPos + 1] : undefined
      if (next && /^[A-Za-z0-9]+$/.test(next)) hash = next
    }
    if (!hash) hash = parsed.searchParams.get('h') ?? ''
    if (!/^\d+$/.test(videoId)) return null
    const embed = new URL(`https://player.vimeo.com/video/${videoId}`)
    if (hash) embed.searchParams.set('h', hash)
    embed.searchParams.set('dnt', '1')
    embed.searchParams.set('title', '0')
    embed.searchParams.set('byline', '0')
    embed.searchParams.set('portrait', '0')
    if (autoplay) {
      embed.searchParams.set('autoplay', '1')
      embed.searchParams.set('controls', '0')
    }
    return embed.toString()
  } catch {
    return null
  }
}

/**
 * A "Show Media" node left on its default "image"/"pdf" type but linked to a YouTube or
 * Vimeo URL is an easy authoring slip (type and URL are set independently in the
 * editor), so the URL decides: such a node still plays as video.
 */
export function detectVideoOverride(configured: MediaType, url: string | undefined): MediaType {
  if (!url) return configured
  if (configured === 'video' || configured === 'audio') return configured
  return youtubeEmbedUrl(url) || vimeoEmbedUrl(url) ? 'video' : configured
}

/** Media type configured on a show-media node, including legacy show-* action types. */
export function configuredMediaType(actionType: string, mediaType: string | undefined): MediaType {
  if (mediaType) return mediaType as MediaType
  if (actionType === 'show-image') return 'image'
  if (actionType === 'show-video') return 'video'
  if (actionType === 'show-audio') return 'audio'
  if (actionType === 'show-pdf') return 'pdf'
  return 'image'
}

/**
 * Finds the material of a media slot. The node id is the stable identity; names are a
 * legacy fallback so authors may freely reuse or rename nodes.
 */
const findMaterial = (materials: MediaMaterial[], nodeId: string, name: string, type: string) =>
  materials.find((m) => m.nodeId === nodeId && m.type === type) ??
  materials.find((m) => m.nodeId === nodeId) ??
  materials.find((m) => !m.nodeId && m.name === name && m.type === type) ??
  materials.find((m) => !m.nodeId && m.name === name)

/** Material a node links to when it is reached (used to detect video and completion rules). */
export const linkedMaterial = (
  materials: MediaMaterial[] | undefined,
  mediaId: string,
  mediaName: string,
  type: MediaType,
) =>
  materials?.find((media) => media.nodeId === mediaId) ??
  materials?.find((media) => !media.nodeId && media.name === mediaName && media.type === type)

/** URLs and types to render for the media on screen; split panes use `<id>:right`. */
export function resolveShownMedia(showing: ShowingMedia, materials: MediaMaterial[]) {
  const leftType = showing.mediaType
  const rightType = showing.mediaTypeRight ?? showing.mediaType
  const rightId = `${showing.mediaId}:right`
  const leftItem = showing.splitSwap
    ? findMaterial(materials, rightId, showing.mediaName, rightType)
    : findMaterial(materials, showing.mediaId, showing.mediaName, leftType)
  const rightItem = showing.splitSwap
    ? findMaterial(materials, showing.mediaId, showing.mediaName, leftType)
    : findMaterial(materials, rightId, showing.mediaName, rightType)
  const primaryItem = findMaterial(materials, showing.mediaId, showing.mediaName, leftType)
  return {
    displayMode: showing.displayMode,
    mediaType: showing.mediaType,
    mediaName: showing.mediaName,
    primaryUrl: primaryItem?.url ?? '',
    skippable: primaryItem?.completionRequired !== true,
    leftUrl: leftItem?.url ?? '',
    rightUrl: rightItem?.url ?? '',
    leftType,
    rightType,
  }
}

export type ResolvedMedia = ReturnType<typeof resolveShownMedia>

export function mediaLog(event: string, details: Record<string, unknown> = {}): void {
  console.log(`[PLC media ${new Date().toISOString()}] ${event}`, {
    performanceMs: Math.round(performance.now()),
    ...details,
  })
}
